import { ReactElement, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import PersonSearch from 'components/UI/Input/PersonSearch/PersonSearch';
import { deepCloneObject } from 'store/utility';
import axiosInstance from '../../../services/axios/axios';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import styles from './NewSponsor.module.scss';
import { Headline6 } from 'components/UI/MDC/Typography/Typography';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { doesUserHaveBotSponsor } from './NewSponsor_api';

/**
 * Routes for New Non-Employee Creation pages
 * @returns {ReactElement} React-router routes for New Non-Employee Creation pages
 */
const NewSponsorSelection = ({ handleNewSponsorESOIDUpdate }) => {
	const [sRows, setRows] = useState([]);

	const reduxState = useSelector(state => state.apps);
	const requesteeUserId = reduxState?.appsData?.[0]?.UserID;
	const hasAppIdOtherThan23And1617 = appsData => {
		if (!Array.isArray(appsData)) {
			return false;
		}

		const excludedIds = new Set([23, 1617]); // Return true if other apps exist besides UPMC Network (23) and EpicCare Link (1617)
		return appsData.some(app => !excludedIds.has(Number(app?.id)));
	};

	const [sErrorMessage, setErrorMessage] = useState('');
	const [hasBotSponsor, setHasBotSponsor] = useState(undefined);
	const [sIsSearching, setIsSearching] = useState(false);
	const [selectedSponsor, setSelectedSponsor] = useState(null);
	const [open, setOpen] = useState(true);
	const [personSearchKey, setPersonSearchKey] = useState(0);

	useEffect(() => {
		if (requesteeUserId !== undefined && requesteeUserId !== null) {
			const fetchBotSponsor = async () => {
				const response = await doesUserHaveBotSponsor(requesteeUserId);
				if (response !== undefined) {
					setHasBotSponsor(response);
				}
			};

			fetchBotSponsor();
		}
	}, [requesteeUserId]);

	const getIsManagerValue = row => {
		const value =
			row?.isManager ??
			row?.Cells?.isManager?.Value ??
			row?.Cells?.IsManager?.Value;

		// In case this ever gets switched to boolean
		if (typeof value === 'boolean') {
			return value;
		}
		// In case this ever gets switched to int
		if (typeof value === 'number') {
			return value === 1;
		}
		// Handle if the value is a string
		if (typeof value === 'string') {
			const normalizedValue = value.toLowerCase();
			return normalizedValue === 'true' || normalizedValue === '1';
		}

		return false;
	};

	const onRowClickedHandler = async packet => {
		if (!getIsManagerValue(packet.payload)) {
			setErrorMessage('Only managers can be selected as sponsors.');
			setPersonSearchKey(prev => prev + 1);
			return;
		}

		setErrorMessage('');
		setSelectedSponsor(packet.payload.Cells);

		setOpen(false);
		handleNewSponsorESOIDUpdate(packet.payload.Cells.F_ESO_ID.Value);
	};

	/**
	 * Helper method filtering rows stored in local state against user input. Adds a property to row objects
	 * used to determine visibility of row.
	 *
	 * @param {Event} e event object raised on input change.
	 * @returns void
	 */
	const indexRows = e => {
		let rowsCopy = deepCloneObject(sRows);
		rowsCopy.forEach(row => {
			const indexed = Object.keys(row.Cells).some(
				key =>
					row.Cells[key].Value &&
					row.Cells[key].Value.toLowerCase().includes(e.target.value),
			);
			row.isDisplayed = indexed;
		});
		setRows(rowsCopy);
	};

	const selectedSponsorName =
		selectedSponsor && selectedSponsor['Name']
			? selectedSponsor['Name'].Value
			: null;

	const onDeleteSelectedSponsorHandler = () => {
		setSelectedSponsor(null);
		setOpen(true);
		setPersonSearchKey(prev => prev + 1);
	};

	const shouldRenderSponsorSelection =
		reduxState?.loading !== undefined &&
		reduxState.loading === false &&
		hasAppIdOtherThan23And1617(reduxState?.appsData) &&
		hasBotSponsor === true;

	return shouldRenderSponsorSelection ? (
		<>
			{selectedSponsorName && (
				<Grid
					container
					direction='column'
					alignItems='center'
					p={2}
					sx={{
						border: '1px solid rgba(211, 211, 211, 0.5)',
						borderRadius: 1,
						width: '100%',
					}}
				>
					<Grid item textAlign='center'>
						<h4>
							<b>Selected sponsor</b>
						</h4>
					</Grid>
					<Grid item>
						{selectedSponsorName}
						<IconButton
							aria-label='delete'
							size='large'
							onClick={onDeleteSelectedSponsorHandler}
						>
							<DeleteIcon fontSize='inherit' />
						</IconButton>
					</Grid>
				</Grid>
			)}
			<Modal
				open={open}
				sx={{
					display: 'flex',
					alignItems: { xs: 'flex-start', sm: 'center' },
					justifyContent: 'center',
					overflow: 'auto',
					p: 2,
				}}
			>
				<Box className={styles.Modal}>
					<Grid
						container
						width='100%'
						justifyContent='center'
						spacing={2}
					>
						<Grid item textAlign='center'>
							<Headline6>
								This account was previously auto-created with no
								sponsor. Additional access beyond EpicCare Link
								requires a sponsor to be identified. Please
								select a sponsor to proceed with the request.
							</Headline6>
						</Grid>
						<Grid item>
							<PersonSearch
								key={personSearchKey}
								className={styles.personSearchOverride}
								label='Sponsor Search (Please select one)'
								dataTable={{
									onTextFieldChangedHandler: indexRows,
									onRowClickedHandler: onRowClickedHandler,
									rows: sRows.filter(
										row => row?.isDisplayed !== false,
									),
								}}
								errorMessage={sErrorMessage}
								isLoading={sIsSearching}
								onButtonClickedHandler={payload => {
									setIsSearching(true);

									axiosInstance
										.post(
											'api/Components/PersonSearch',
											payload,
										)
										.then(response => {
											switch (response.status) {
												case 200:
													switch (
														response.data
															.responseCode
													) {
														case 200:
															setRows(
																response.data
																	.payload,
															);
															setErrorMessage('');
															break;
														default:
															setErrorMessage(
																response.data
																	.messages[0]
																	.Value,
															);
													}
													break;
												default:
													break;
											}
										})
										.catch(error => {
											setErrorMessage(
												error &&
													error.response &&
													error.response.data
													? error.response.data
															.payload
													: error.message,
											);
										})
										.finally(() => {
											setIsSearching(false);
										});
								}}
								requestTarget='REQUESTEE'
							/>
						</Grid>
					</Grid>
				</Box>
			</Modal>
		</>
	) : null;
};

export default NewSponsorSelection;
